import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiReports } from "../api/reports";
import SchedulerTextBreakdown from "./SchedulerTextBreakdown";
import {
  monitoringToday, monitoringRange, monitoringDates, monitoringDuration, monitoringDateLabel,
  monitoringTime, monitoringPosition, shiftMonitoringDate, monitoringStart,
  monitoringEmptyDay, monitoringMarkers, monitoringAverages, monitoringTextsPerScheduled,
  activityBarParts, activityPolicyPreview, scoreBadgeClass, monitoringScoreLabel,
} from "./timeMonitoringUtils";

const control = "tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-bg-white focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-opacity-40";
const nameOf = (person) => person.nickname?.trim() || person.name;
const rangeLabel = (from, to) => from === to ? monitoringDateLabel(from, { weekday: "long", year: "numeric" })
  : `${monitoringDateLabel(from, { year: "numeric" })} – ${monitoringDateLabel(to, { year: "numeric" })}`;

// Breaks between engaged segments, large enough to hover (>= 1 minute).
function activityGaps(segments) {
  const gaps = [];
  for (let i = 1; i < segments.length; i++) {
    const a = new Date(segments[i - 1].end).getTime();
    const b = new Date(segments[i].start).getTime();
    if (b - a >= 60000) { gaps.push({ a, b }); }
  }
  return gaps;
}

function ActivityBar({ day, onClick, compact = false }) {
  const segments = day?.segments || [];
  const gaps = activityGaps(segments);
  const markers = day ? monitoringMarkers(day) : [];
  const ticks = compact ? markers : [{ label: "12am", position: 0 }, ...markers, { label: "12am", position: 100 }];
  const Wrapper = onClick ? "button" : "div";
  return <Wrapper type={onClick ? "button" : undefined} onClick={onClick}
    role={onClick ? undefined : "img"}
    aria-label={`${monitoringDuration(day?.engagedSeconds)} engaged; 8am and 6pm ET markers${onClick ? ", view day details" : ""}`}
    className={`tw-block tw-w-full tw-text-left ${onClick ? "tw-rounded focus:tw-ring-2 focus:tw-ring-blue-500" : ""}`}>
    <span className={`tw-relative tw-block tw-overflow-hidden tw-rounded tw-bg-gray-100 ${compact ? "tw-h-5" : "tw-h-6"}`}>
      {[25, 50, 75].map((left) => <span key={left} aria-hidden="true" className="tw-absolute tw-h-full tw-border-l tw-border-white" style={{ left: `${left}%` }} />)}
      {segments.map((segment, i) => <span key={i} title={`${monitoringTime(segment.start)} – ${monitoringTime(segment.end)}`}
        className="tw-absolute tw-h-full tw-rounded-sm tw-bg-green-600"
        style={{ left: `${monitoringPosition(segment.start, day)}%`, width: `${monitoringPosition(segment.end, day) - monitoringPosition(segment.start, day)}%`, minWidth: 2 }} />)}
      {gaps.map((gap, i) => <span key={`gap${i}`} aria-hidden="true"
        title={`Break ${monitoringTime(new Date(gap.a), day)} – ${monitoringTime(new Date(gap.b), day)} (${monitoringDuration((gap.b - gap.a) / 1000)})`}
        className="tw-absolute tw-inset-y-0 tw-cursor-help"
        style={{ left: `${monitoringPosition(new Date(gap.a), day)}%`, width: `${Math.max(0.3, monitoringPosition(new Date(gap.b), day) - monitoringPosition(new Date(gap.a), day))}%` }} />)}
      {markers.map((marker) => <span key={marker.label} title={`${marker.label} ET`}
        className="tw-absolute tw-inset-y-0 tw-z-10 tw-border-l-2 tw-border-dashed tw-border-blue-900" style={{ left: `${marker.position}%` }} />)}
    </span>
    <span className="tw-relative tw-block tw-h-4 tw-mt-1 tw-text-gray-500" style={{ fontSize: 10 }} aria-hidden="true">
      {ticks.map((tick) => <span key={tick.label + tick.position} className="tw-absolute tw-whitespace-nowrap" style={{
        left: `${tick.position}%`, transform: tick.position === 0 ? "none" : tick.position === 100 ? "translateX(-100%)" : "translateX(-50%)",
      }}>{tick.label}</span>)}
    </span>
  </Wrapper>;
}

function ScoreBadge({ score }) {
  if (score === null || score === undefined) {return null;}
  return <span className={`tw-inline-block tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-whitespace-nowrap ${scoreBadgeClass(score)}`}
    title={`AI productivity rating for this day`}
    style={{ fontVariantNumeric: "tabular-nums" }}>{monitoringScoreLabel(score)}</span>;
}

function ActivityCategoryBar({ totals, height = "tw-h-5" }) {
  const { parts, total } = activityBarParts(totals);
  if (!total) {return null;}
  return <span role="img" aria-label={parts.map((part) => `${part.label} ${monitoringDuration(part.seconds)}`).join(", ")}
    className={`tw-relative tw-block tw-overflow-hidden tw-rounded tw-bg-gray-100 ${height}`}>
    {parts.map((part) => <span key={part.key} title={`${part.label} ${monitoringDuration(part.seconds)}`}
      className="tw-absolute tw-inset-y-0" style={{ left: `${part.left}%`, width: `${part.width}%`, background: part.color }} />)}
  </span>;
}

function ActivityCountRow({ item, showCalls = true }) {
  const counts = [
    showCalls ? ["Calls", item.calls, item.callSeconds ? ` (${monitoringDuration(item.callSeconds)} talk)` : ""] : null,
    ["Missed", item.missedCalls, ""],
    ["Texts", item.texts, ""],
    ["Saves", item.saves, ""],
    ["Appts", item.appointments, ""],
  ].filter(([label, value]) => label && value > 0);
  if (!counts.length) {return <span className="tw-text-xs tw-text-gray-400">No calls, texts or saves recorded</span>;}
  return <span className="tw-text-xs tw-text-gray-600" style={{ fontVariantNumeric: "tabular-nums" }}>
    {counts.map(([label, value, note]) => `${label} ${value}${note}`).join(" · ")}
  </span>;
}

function ActivityBreakdown({ person, day, loading, error, activity, timeline, narrative, score, narrativeLoading, narrativeError, onRequestNarrative,
  verdict, verdictNote, verdictSaving, verdictError, canJudge, onVerdictNote, onVerdict, onClearVerdict }) {
  const empty = !activity || !activity.blocks?.length && !activity.calls && !activity.texts && !activity.saves && !activity.appointments;
  return <section aria-label="Activity breakdown" className="tw-my-5">
    <div className="tw-flex tw-justify-between tw-items-baseline tw-gap-3 tw-mb-3">
      <h3 className="tw-text-base tw-font-semibold tw-m-0">What {nameOf(person)} did {score !== null ? <ScoreBadge score={score} /> : null}</h3>
      <span className="tw-text-xs tw-text-gray-500">{monitoringDateLabel(day, { weekday: "long" })} · Eastern time</span>
    </div>
    {loading ? <p role="status" className="tw-text-sm tw-text-gray-500 tw-m-0">Loading activity breakdown…</p>
      : error ? <p role="alert" className="tw-text-sm tw-text-red-700 tw-m-0">{error}</p>
        : empty ? <p className="tw-text-sm tw-text-gray-500 tw-m-0">No recorded activity on this day.</p>
          : <>
            <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4 tw-mb-3">
              {timeline && <div className="tw-mb-3"><ActivityBar day={timeline} /></div>}
              {narrative && <p className="tw-text-lg tw-leading-relaxed tw-text-gray-800 tw-mb-3">{narrative}</p>}
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mb-2">
                <strong style={{ fontVariantNumeric: "tabular-nums" }}>{monitoringDuration(activity.engagedSeconds)} engaged</strong>
                <ActivityCountRow item={activity} />
              </div>
              <ActivityCategoryBar totals={activity} height="tw-h-6" />
              <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-mt-2">
                {activityBarParts(activity).parts.map((part) => <span key={part.key} className="tw-text-xs tw-text-gray-600">
                  <span aria-hidden="true" className="tw-inline-block tw-w-2 tw-h-2 tw-rounded-sm tw-mr-1" style={{ background: part.color }} />
                  {part.label} <strong style={{ fontVariantNumeric: "tabular-nums" }}>{monitoringDuration(part.seconds)}</strong>
                </span>)}
                {activity.policies > 0 && <span className="tw-text-xs tw-text-gray-600">Policies touched <strong>{activity.policies}</strong></span>}
              </div>
            </div>
            <div className="tw-mt-3"><SchedulerTextBreakdown counts={activity.textKinds} /></div>
            {canJudge && <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-3">
              <span className="tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">Your call:</span>
              <label className="tw-sr-only" htmlFor="verdict-note">Reason</label>
              <input id="verdict-note" type="text" maxLength={200} value={verdictNote}
                placeholder="Optional reason — teaches the AI your standard"
                className={`${control} tw-flex-1 tw-min-w-[180px]`} onChange={onVerdictNote} />
              <button type="button" onClick={() => onVerdict("productive")} disabled={verdictSaving}
                aria-pressed={verdict?.verdict === "productive"}
                className={`${control} ${verdict?.verdict === "productive" ? "tw-bg-green-700 tw-text-white tw-border-green-700" : ""}`}>👍 Productive</button>
              <button type="button" onClick={() => onVerdict("unproductive")} disabled={verdictSaving}
                aria-pressed={verdict?.verdict === "unproductive"}
                className={`${control} ${verdict?.verdict === "unproductive" ? "tw-bg-red-700 tw-text-white tw-border-red-700" : ""}`}>👎 Unproductive</button>
              {verdict && <button type="button" onClick={onClearVerdict} disabled={verdictSaving} className={control}>Clear</button>}
              {verdictError && <p role="alert" className="tw-text-xs tw-text-red-700 tw-m-0 tw-w-full">{verdictError}</p>}
            </div>}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mt-3">
              <button type="button" onClick={onRequestNarrative} disabled={narrativeLoading}
                title="Regenerate the AI assessment with your latest verdict calibration"
                className="tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-rounded-md tw-border-primary tw-text-primary hover:tw-bg-blue-50 disabled:tw-opacity-50">
                {narrativeLoading ? "Summarising…" : narrative ? "Regenerate" : "AI summary"}
              </button>
              {narrativeError && <p role="alert" className="tw-text-xs tw-text-red-700 tw-m-0">{narrativeError}</p>}
            </div>
            {activity.blocks?.length ? <ol className="tw-border tw-border-gray-200 tw-rounded-lg tw-divide-y tw-divide-gray-100 tw-mt-3">
              {activity.blocks.map((block) => {
                const parts = activityBarParts(block);
                return <li key={block.start} className="tw-px-4 tw-py-3">
                  <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-baseline tw-gap-2">
                    <time className="tw-text-sm tw-font-semibold tw-whitespace-nowrap" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {monitoringTime(block.start)} – {monitoringTime(block.end)}</time>
                    <strong className="tw-text-sm" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {monitoringDuration((new Date(block.end) - new Date(block.start)) / 1000)}
                      {parts.total ? <span className="tw-text-xs tw-text-gray-400 tw-font-normal tw-ml-1">· {monitoringDuration(parts.total)} on pages</span> : null}
                    </strong>
                  </div>
                  <div className="tw-my-2"><ActivityCategoryBar totals={block} /></div>
                  <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-baseline tw-gap-x-4 tw-gap-y-1">
                    <ActivityCountRow item={block} />
                    {block.policies?.length > 0 && <span className="tw-text-xs tw-text-gray-600" title={block.policies.join(", ")}>
                      {activityPolicyPreview(block.policies)}
                    </span>}
                  </div>
                </li>;})}
            </ol> : <p className="tw-text-xs tw-text-gray-500 tw-m-0">No engaged blocks on this day; all recorded activity was calls, texts or saves outside page sessions.</p>}
          </>}
  </section>;
}

function SchedulerTimeDetail({ person, selectedDay, from, to, onClose, onScore }) {
  const dialog = useRef(null);
  const selectedRow = useRef(null);
  const dates = useMemo(() => monitoringDates(from, to), [from, to]);
  const byDay = useMemo(() => Object.fromEntries(person.days.map((day) => [day.day, day])), [person]);
  const averages = monitoringAverages(person);
  const [activityDay, setActivityDay] = useState(selectedDay || (from === to ? from : null));
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [narrative, setNarrative] = useState(null);
  const [score, setScore] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeError, setNarrativeError] = useState("");

  const resetNarrative = () => { setNarrative(null); setScore(null); setNarrativeError(""); };
  const [verdict, setVerdict] = useState(null);
  const [verdictNote, setVerdictNote] = useState("");
  const [verdictSaving, setVerdictSaving] = useState(false);
  const [verdictError, setVerdictError] = useState("");

  useEffect(() => {
    if (!activityDay) { setVerdict(null); setVerdictNote(""); setVerdictError(""); return; }
    let cancelled = false;
    setVerdict(null);
    setVerdictNote("");
    setVerdictError("");
    apiReports.getTimeMonitoringVerdict(person.userId, activityDay).then((response) => {
      if (cancelled) { return; }
      if (response.data.status === 200 && response.data.result?.day === activityDay) {
        if (response.data.result.verdict) { setVerdict(response.data.result); }
        setVerdictNote(response.data.result.note || "");
      }
    }).catch(() => { if (!cancelled) { setVerdictError("Your verdict could not be loaded."); } });
    return () => { cancelled = true; };
  }, [person.userId, activityDay]);

  const saveVerdict = (choice) => {
    if (!activityDay || verdictSaving) { return; }
    setVerdictSaving(true);
    setVerdictError("");
    apiReports.saveTimeMonitoringVerdict(person.userId, activityDay, choice, verdictNote).then((response) => {
      if (response.data.status === 200) { setVerdict({ day: activityDay, verdict: choice }); }
      else { throw new Error("invalid verdict response"); }
    }).catch(() => { setVerdictError("Could not save your verdict."); })
      .finally(() => { setVerdictSaving(false); });
  };

  const clearVerdict = () => {
    if (!activityDay || verdictSaving) { return; }
    setVerdictSaving(true);
    setVerdictError("");
    apiReports.deleteTimeMonitoringVerdict(person.userId, activityDay).then((response) => {
      if (response.data.status === 200) { setVerdict(null); }
      else { throw new Error("invalid verdict response"); }
    }).catch(() => { setVerdictError("Could not clear your verdict."); })
      .finally(() => { setVerdictSaving(false); });
  };

  useEffect(() => {
    if (!activityDay) { setActivity(null); setActivityError(""); resetNarrative(); return; }
    let cancelled = false;
    setActivityLoading(true);
    setActivityError("");
    setActivity(null);
    resetNarrative();
    apiReports.getTimeMonitoringActivity(person.userId, activityDay).then((response) => {
      if (cancelled) { return; }
      if (response.data.status === 200 && response.data.result?.day === activityDay) { setActivity(response.data.result); }
      else { throw new Error("invalid activity response"); }
    }).catch(() => { if (!cancelled) { setActivityError("Activity breakdown could not be loaded."); } })
      .finally(() => { if (!cancelled) { setActivityLoading(false); } });
    return () => { cancelled = true; };
  }, [person.userId, activityDay]);

  const loadNarrative = () => {
    if (!activityDay) { return; }
    let cancelled = false;
    apiReports.getTimeMonitoringNarrative(person.userId, activityDay).then((response) => {
      if (cancelled) { return; }
      if (response.data.status === 200 && response.data.result?.day === activityDay) {
        setNarrative(response.data.result.narrative || null);
        setScore(response.data.result.narrative ? response.data.result.score : null);
      }
    }).catch(() => {}); // the stored narrative is a free extra; failures just leave the button
    return () => { cancelled = true; };
  };

  useEffect(() => { return loadNarrative(); }, [person.userId, activityDay]);

  const requestNarrative = () => {
    if (!activityDay || narrativeLoading) { return; }
    setNarrativeLoading(true);
    setNarrativeError("");
    apiReports.generateTimeMonitoringNarrative(person.userId, activityDay).then((response) => {
      if (response.data.status === 200 && response.data.result?.day === activityDay) {
        setNarrative(response.data.result.narrative || null);
        setScore(response.data.result.narrative ? response.data.result.score : null);
        onScore?.(person.userId, response.data.result.narrative ? response.data.result.score : null);
      }
      else { throw new Error("invalid narrative response"); }
    }).catch(() => { setNarrativeError("AI summary could not be generated."); })
      .finally(() => { setNarrativeLoading(false); });
  };

  useEffect(() => {
    const node = dialog.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    node.showModal();
    selectedRow.current?.scrollIntoView({ block: "nearest" });
    return () => { node.close(); document.body.style.overflow = overflow; };
  }, []);

  return <dialog ref={dialog} onCancel={onClose} aria-labelledby="time-detail-title"
    className="tw-p-0 tw-border-0 tw-rounded-xl tw-shadow-2xl tw-text-gray-800"
    style={{ width: "min(1040px, calc(100vw - 24px))", maxHeight: "calc(100vh - 32px)" }}
    onClick={(event) => {
      if (event.target !== dialog.current) {return;}
      const r = dialog.current.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) {onClose();}
    }}>
    <div className="tw-sticky tw-top-0 tw-z-20 tw-flex tw-items-start tw-justify-between tw-gap-4 tw-p-6 tw-border-b tw-border-gray-200" style={{ background: "#f4f6f8" }}>
      <div><h2 id="time-detail-title" className="tw-text-2xl tw-font-semibold tw-m-0 tw-inline-flex tw-items-center tw-gap-2">{nameOf(person)} {score !== null ? <ScoreBadge score={score} /> : null}</h2>
        <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-mb-0">{rangeLabel(from, to)} · Eastern time</p></div>
      <button type="button" onClick={onClose} className={control} aria-label="Close time monitoring details">✕</button>
    </div>
    <div className="tw-p-6">
      <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 tw-gap-x-6 tw-gap-y-4">
        {[["Engaged in this period", monitoringDuration(person.engagedSeconds)], ["Call talk time", monitoringDuration(person.callSeconds)],
          ["Policies scheduled", person.scheduled], ["Texts sent", person.texts],
          ["Texts / scheduled", monitoringTextsPerScheduled(person.texts, person.scheduled)], ["Calls / missed", `${person.calls} / ${person.missed}`],
          ["Reports saved", person.reportEdits]].map(([label, value]) => <div key={label}>
          <div className="tw-text-xs tw-text-gray-500">{label}</div><strong className="tw-text-xl" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</strong>
        </div>)}
      </div>
      <div className="tw-mt-4"><SchedulerTextBreakdown counts={person.textKinds} /></div>
      <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-rounded-lg tw-bg-gray-50 tw-p-4 tw-my-5" aria-label="Daily averages">
        <div title="Average engaged time on days with at least one engaged hour">
          <div className="tw-text-xs tw-text-gray-500">Avg hours / day</div>
          <strong className="tw-text-xl">{averages.seconds == null ? "—" : monitoringDuration(averages.seconds)}</strong>
        </div>
        <div title="All policies scheduled in the period divided by days with at least one engaged hour">
          <div className="tw-text-xs tw-text-gray-500">Avg scheduled / day</div>
          <strong className="tw-text-xl">{averages.scheduled == null ? "—" : averages.scheduled.toFixed(1)}</strong>
        </div>
        <div><div className="tw-text-xs tw-text-gray-500">Days ≥1h</div><strong className="tw-text-xl">{averages.days} / {dates.length}</strong></div>
      </div>
      {activityDay
        ? <ActivityBreakdown person={person} day={activityDay} loading={activityLoading} error={activityError} activity={activity}
          timeline={byDay[activityDay] || monitoringEmptyDay(activityDay)}
          narrative={narrative} score={score} narrativeLoading={narrativeLoading} narrativeError={narrativeError} onRequestNarrative={requestNarrative}
          verdict={verdict} verdictNote={verdictNote} verdictSaving={verdictSaving} verdictError={verdictError}
          canJudge={activityDay < monitoringToday()} onVerdictNote={(e) => setVerdictNote(e.target.value)}
          onVerdict={saveVerdict} onClearVerdict={clearVerdict} />
        : <p className="tw-text-sm tw-text-gray-500 tw-my-4 tw-mb-5">Select a day below to see the activity breakdown.</p>}
      <section aria-label="Daily activity bars">
        <div className="tw-flex tw-justify-between tw-gap-3 tw-mb-3">
          <h3 className="tw-text-base tw-font-semibold tw-m-0">Daily activity</h3><span className="tw-text-xs tw-text-gray-500">Eastern time</span>
        </div>
        <div className="tw-overflow-y-auto tw-border tw-border-gray-200 tw-rounded-lg" style={{ maxHeight: "50vh", overscrollBehavior: "contain" }}>
          {dates.map((date) => {
            const day = byDay[date] || monitoringEmptyDay(date);
            return <article key={date} ref={date === selectedDay ? selectedRow : undefined} aria-label={monitoringDateLabel(date, { weekday: "long" })}
              className={`tw-px-4 tw-py-3 tw-border-b tw-border-gray-100 ${date === activityDay || date === selectedDay ? "tw-bg-blue-50" : ""}`}>
              <div className="tw-flex tw-justify-between tw-items-baseline tw-gap-3 tw-mb-2">
                <button type="button" onClick={() => setActivityDay(date)} aria-pressed={date === activityDay}
                  title="Show the activity breakdown for this day"
                  className="tw-text-sm tw-font-semibold tw-rounded hover:tw-underline focus:tw-ring-2 focus:tw-ring-blue-500">
                  <time dateTime={date}>{monitoringDateLabel(date, { weekday: "short" })}</time>
                </button>
                <strong className="tw-text-sm" style={{ fontVariantNumeric: "tabular-nums" }}>{monitoringDuration(day.engagedSeconds)}</strong>
              </div>
              <ActivityBar day={day} />
              <div className="tw-flex tw-flex-wrap tw-justify-between tw-gap-1 tw-text-xs tw-text-gray-500 tw-mt-1">
                <span>First {monitoringTime(day.first)} · Latest {monitoringTime(day.last, day)}</span>
                <span>Calls {monitoringDuration(day.callSeconds)}</span>
              </div>
            </article>;
          })}
        </div>
      </section>
    </div>
  </dialog>;
}

export default function TimeMonitoringTab() {
  const today = monitoringToday();
  const [selection, setSelection] = useState(() => ({ mode: "day", anchor: today, from: today, to: today }));
  const [draft, setDraft] = useState({ from: today, to: today });
  const [rangeError, setRangeError] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "engagedSeconds", direction: -1 });
  const [selected, setSelected] = useState(null);
  const [scores, setScores] = useState({});
  const [dayRegenerating, setDayRegenerating] = useState(false);
  const [dayRegenMessage, setDayRegenMessage] = useState("");
  const [dayRegenToken, setDayRegenToken] = useState(0);
  const [teamNarrative, setTeamNarrative] = useState(null);
  const [teamScore, setTeamScore] = useState(null);
  const [teamGenerating, setTeamGenerating] = useState(false);
  const [teamError, setTeamError] = useState("");
  const ready = data?.selectionKey === `${selection.from}/${selection.to}`;
  const dates = useMemo(() => monitoringDates(selection.from, selection.to), [selection.from, selection.to]);

  const choose = (mode, anchor) => {
    anchor = anchor < monitoringStart ? monitoringStart : anchor > today ? today : anchor;
    setSelected(null);
    setRangeError("");
    setDayRegenMessage("");
    if (mode === "custom") {
      setDraft({ from: selection.from, to: selection.to });
      setSelection({ ...selection, mode });
    } else {
      setSelection({ mode, anchor, ...monitoringRange(mode, anchor, data?.today || today) });
    }
  };
  const move = (direction) => {
    const { mode, anchor } = selection;
    let next = shiftMonitoringDate(anchor, direction * (mode === "week" ? 7 : 1));
    if (mode === "month") {
      const d = new Date(`${anchor.slice(0, 7)}-01T12:00:00Z`);
      d.setUTCMonth(d.getUTCMonth() + direction);
      next = d.toISOString().slice(0, 10);
    }
    choose(mode, next);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    apiReports.getTimeMonitoring(selection.from, selection.to).then((response) => {
      const result = response.data.result;
      if (response.data.status !== 200 || !Array.isArray(result?.schedulers) ||
        !Number.isInteger(result.totalScheduled) || !Number.isInteger(result.otherScheduled) || !Number.isInteger(result.totalReportsSaved)) {throw new Error("Invalid monitoring response");}
      if (!cancelled) {setData({ ...response.data.result, selectionKey: `${selection.from}/${selection.to}` });}
    }).catch(() => {if (!cancelled) {setError("Time monitoring could not be loaded.");}})
      .finally(() => {if (!cancelled) {setLoading(false);}});
    return () => { cancelled = true; };
  }, [selection.from, selection.to, retry]);

  const singleDayScores = selection.from === selection.to;
  useEffect(() => {
    if (!ready || !singleDayScores) { setScores({}); return; }
    let cancelled = false;
    apiReports.getTimeMonitoringScores(selection.from).then((response) => {
      if (!cancelled && response.data.status === 200 && response.data.result?.day === selection.from) {
        setScores(response.data.result.scores || {});
      }
    }).catch(() => { if (!cancelled) { setScores({}); } });
    return () => { cancelled = true; };
  }, [ready, singleDayScores, selection.from, retry]);

  useEffect(() => {
    if (!ready || !singleDayScores) { setTeamNarrative(null); setTeamScore(null); setTeamError(""); return; }
    let cancelled = false;
    apiReports.getTimeMonitoringTeamNarrative(selection.from).then((response) => {
      if (cancelled) { return; }
      if (response.data.status === 200 && response.data.result?.day === selection.from) {
        setTeamNarrative(response.data.result.narrative || null);
        setTeamScore(response.data.result.narrative ? response.data.result.score : null);
      }
    }).catch(() => {}); // stored team summary is a free extra; failures just leave the button
    return () => { cancelled = true; };
  }, [ready, singleDayScores, selection.from, retry]);

  const generateTeamNarrative = () => {
    if (!singleDayScores || teamGenerating) { return; }
    setTeamGenerating(true);
    setTeamError("");
    apiReports.generateTimeMonitoringTeamNarrative(selection.from).then((response) => {
      if (response.data.status === 200 && response.data.result?.day === selection.from) {
        setTeamNarrative(response.data.result.narrative || null);
        setTeamScore(response.data.result.narrative ? response.data.result.score : null);
      }
      else { throw new Error("invalid team narrative response"); }
    }).catch(() => { setTeamError("Team summary could not be generated."); })
      .finally(() => { setTeamGenerating(false); });
  };

  const people = ready ? data.schedulers : [];
  const visible = useMemo(() => people.filter((p) => `${p.name} ${p.nickname || ""} ${p.username || ""}`.toLowerCase().includes(search.toLowerCase())).sort((a, b) => {
    const comparison = sort.key === "name" ? nameOf(a).localeCompare(nameOf(b)) : (a[sort.key] || 0) - (b[sort.key] || 0);
    return comparison * sort.direction || nameOf(a).localeCompare(nameOf(b));
  }), [people, search, sort]);
  const totals = people.reduce((sum, p) => {
    for (const key of ["engagedSeconds", "callSeconds", "scheduled", "texts"]) {sum[key] += p[key] || 0;}
    return sum;
  }, { engagedSeconds: 0, callSeconds: 0, scheduled: 0, texts: 0 });
  const singleDay = selection.from === selection.to;
  const isToday = singleDay && selection.from === data?.today;
  const sortBy = (key) => setSort({ key, direction: sort.key === key ? -sort.direction : key === "name" ? 1 : -1 });
  const nextRange = monitoringRange(selection.mode, selection.anchor, data?.today || today);
  const nextDisabled = selection.to >= (data?.today || today) || nextRange.from > (data?.today || today);
  const openDetail = (person, day) => setSelected({ userId: person.userId, day });

  const regenerateDay = () => {
    if (!singleDay || dayRegenerating) { return; }
    setDayRegenerating(true);
    setDayRegenMessage("");
    apiReports.regenerateTimeMonitoringDay(selection.from).then((response) => {
      if (response.data.status !== 200 || response.data.result?.day !== selection.from) { throw new Error("invalid response"); }
      const result = response.data.result;
      setDayRegenMessage(`Regenerated ${result.regenerated} assessment${result.regenerated === 1 ? "" : "s"}` +
        (result.teamRegenerated ? " and the team summary" : "") +
        (result.skippedNoActivity ? `, skipped ${result.skippedNoActivity} with no activity` : "") +
        (result.teamSkippedNoActivity ? ", team summary skipped (no activity)" : "") +
        (result.failed ? `, ${result.failed} failed` : "") + ".");
      setScores({});
      setRetry((v) => v + 1); // refresh monitoring data + score badges
      setDayRegenToken((v) => v + 1); // remount an open detail dialog so it reloads
    }).catch(() => { setDayRegenMessage("Whole-day regeneration failed."); })
      .finally(() => { setDayRegenerating(false); });
  };
  const selectedPerson = people.find((p) => p.userId === selected?.userId);

  return <section aria-label="Time Monitoring" className="tw-rounded-xl tw-border tw-border-gray-200 tw-overflow-hidden tw-mt-4 tw-text-gray-800" style={{ background: "#f6f7f5" }}>
    <div className="tw-p-5 sm:tw-p-6">
      <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-start tw-gap-4">
        <h2 className="tw-text-2xl tw-font-semibold tw-m-0" style={{ color: "#23364b" }}>Time Monitoring</h2>
        {ready && <span className="tw-text-xs tw-text-gray-500">Updated {monitoringTime(data.asOf)}</span>}
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-5">
        {[["Today", "day", today], ["Yesterday", "day", shiftMonitoringDate(today, -1)], ["This week", "week", today], ["This month", "month", today]].map(([label, mode, anchor]) => {
          const range = monitoringRange(mode, anchor, today);
          const active = selection.mode === mode && selection.from === range.from && selection.to === range.to;
          return <button key={label} type="button" aria-pressed={active} onClick={() => choose(mode, anchor)}
            className={`${control} ${active ? "tw-bg-blue-900 tw-text-white tw-border-blue-900" : "hover:tw-bg-gray-100"}`}>{label}</button>;
        })}
        <span className="tw-h-6 tw-border-l tw-border-gray-300 tw-mx-1" aria-hidden="true" />
        <label className="tw-sr-only" htmlFor="time-period">Period</label>
        <select id="time-period" className={control} value={selection.mode} onChange={(e) => choose(e.target.value, selection.from)}>
          <option value="day">Day</option><option value="week">Week</option><option value="month">Month</option><option value="custom">Custom range</option>
        </select>
        {selection.mode !== "custom" && <>
          <button type="button" aria-label="Previous period" className={control} disabled={selection.from <= monitoringStart} onClick={() => move(-1)}>‹</button>
          <label className="tw-sr-only" htmlFor="time-anchor">{selection.mode === "month" ? "Monitoring month" : "Monitoring date"}</label>
          <input id="time-anchor" type={selection.mode === "month" ? "month" : "date"} className={control}
            min={selection.mode === "month" ? monitoringStart.slice(0, 7) : monitoringStart}
            max={selection.mode === "month" ? today.slice(0, 7) : today} value={selection.mode === "month" ? selection.anchor.slice(0, 7) : selection.anchor}
            onChange={(e) => {if (e.target.value) {choose(selection.mode, selection.mode === "month" ? `${e.target.value}-01` : e.target.value);}}} />
          <button type="button" aria-label="Next period" className={control} disabled={nextDisabled} onClick={() => move(1)}>›</button>
        </>}
      </div>
      {selection.mode === "custom" && <form className="tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-mt-3" onSubmit={(e) => {
        e.preventDefault();
        if (!draft.from || !draft.to || draft.from < monitoringStart || draft.from > draft.to || draft.to > today || (new Date(draft.to) - new Date(draft.from)) / 86400000 > 365) {
          setRangeError("Choose up to 366 days between 1 September 2026 and today."); return;
        }
        setRangeError(""); setSelected(null); setSelection({ mode: "custom", anchor: draft.from, ...draft });
      }}>
        <label className="tw-text-xs tw-text-gray-600" htmlFor="time-from">From
          <input id="time-from" type="date" required min={monitoringStart} max={today} value={draft.from} className={`${control} tw-block tw-mt-1`}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></label>
        <label className="tw-text-xs tw-text-gray-600" htmlFor="time-to">Through
          <input id="time-to" type="date" required min={draft.from} max={today} value={draft.to} className={`${control} tw-block tw-mt-1`}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></label>
        <button type="submit" className={`${control} tw-bg-blue-900 tw-text-white`}>Apply range</button>
        <span className="tw-text-xs tw-text-gray-500 tw-pb-2">Up to 366 days · all dates ET</span>
      </form>}
      {rangeError && <p role="alert" className="tw-text-sm tw-text-red-700 tw-mt-2 tw-mb-0">{rangeError}</p>}
    </div>

    <div className="tw-px-5 sm:tw-px-6 tw-pb-6" aria-busy={loading}>
      <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2 tw-mb-3">
        <h3 className="tw-text-base tw-font-semibold tw-m-0">{rangeLabel(selection.from, selection.to)}</h3>
        <span className="tw-text-xs tw-text-gray-500">Eastern time · {dates.length} {dates.length === 1 ? "day" : "days"}{selection.to === today ? " · through the latest update" : ""}</span>
      </div>
      {error ? <div role="alert" className="tw-p-5 tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-text-red-800 tw-text-sm">
        {error} <button type="button" className={`${control} tw-ml-3`} onClick={() => setRetry((v) => v + 1)}>Retry</button></div>
        : !ready || loading ? <div role="status" className="tw-py-16 tw-text-center tw-text-gray-500">Loading scheduler activity…</div>
          : <>
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-5 tw-rounded-xl tw-overflow-hidden tw-mb-5" style={{ background: "#23364b", color: "#fff" }}>
              {[["Engaged time", monitoringDuration(totals.engagedSeconds), ""], ["Call talk time", monitoringDuration(totals.callSeconds), ""],
                ["Policies scheduled", data.totalScheduled, "Company total"], ["Texts sent", totals.texts, ""],
                ["Reports saved", data.totalReportsSaved, `${people.filter((p) => p.days.length).length} of ${people.length} with activity`]].map(([label, value, note], i) =>
                <div key={label} className={`tw-p-3 sm:tw-p-5 tw-border-white tw-border-opacity-10 ${i ? "md:tw-border-l" : "tw-col-span-2 md:tw-col-span-1"}`}>
                  <div className="tw-text-xs sm:tw-text-sm tw-opacity-80">{label}</div>
                  <strong className="tw-block tw-text-2xl sm:tw-text-3xl tw-mt-1 tw-font-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</strong>
                  {note && <span className="tw-block tw-text-xs tw-opacity-60 tw-mt-2">{note}</span>}
                </div>)}
            </div>
            {singleDay && <section aria-label="Team summary" className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-p-4 tw-mb-5">
              <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-2 tw-mb-2">
                <h3 className="tw-text-base tw-font-semibold tw-m-0 tw-inline-flex tw-items-center tw-gap-2">
                  Team summary {teamScore !== null ? <ScoreBadge score={teamScore} /> : null}
                </h3>
                <button type="button" onClick={generateTeamNarrative} disabled={teamGenerating}
                  title="Generate an AI assessment of the whole team's day"
                  className="tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-rounded-md tw-border-primary tw-text-primary hover:tw-bg-blue-50 disabled:tw-opacity-50">
                  {teamGenerating ? "Summarising…" : teamNarrative ? "Regenerate" : "AI summary"}
                </button>
              </div>
              {teamError && <p role="alert" className="tw-text-xs tw-text-red-700 tw-m-0">{teamError}</p>}
              {teamNarrative && <p className="tw-text-lg tw-leading-relaxed tw-text-gray-800 tw-m-0">{teamNarrative}</p>}
            </section>}
            <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-overflow-hidden">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-p-4 tw-border-b tw-border-gray-200">
                <div><h3 className="tw-text-base tw-font-semibold tw-m-0">Scheduler activity <span className="tw-text-gray-400 tw-font-normal">{visible.length} / {people.length}</span></h3>
                  <p className="tw-text-xs tw-text-gray-500 tw-mb-0 tw-mt-1">Select a name or activity bar to explore the day.</p></div>
                <div className="tw-flex tw-items-center tw-gap-2">
                  {dayRegenMessage && <span role="status" className="tw-text-xs tw-text-gray-500 tw-max-w-[220px]">{dayRegenMessage}</span>}
                  {singleDay && <button type="button" onClick={regenerateDay} disabled={dayRegenerating}
                    title="Regenerate this day's AI assessments for every scheduler, using your latest verdict calibration"
                    className={`${control} tw-whitespace-nowrap ${dayRegenerating ? "tw-opacity-50" : ""}`}>
                    {dayRegenerating ? "Regenerating day…" : "Regenerate whole day"}
                  </button>}
                  <label className="tw-sr-only" htmlFor="time-search">Find a scheduler</label>
                  <input id="time-search" type="search" placeholder="Find a scheduler…" className={control} value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
              </div>
              <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-sm" style={{ minWidth: singleDay ? 1050 : Math.max(1050, dates.length * 25 + 580), tableLayout: "fixed" }}>
                  <colgroup><col style={{ width: 180 }} /><col style={{ width: 100 }} /><col />
                    <col style={{ width: 110 }} /><col style={{ width: 110 }} /><col style={{ width: 80 }} /></colgroup>
                  <thead className="tw-bg-gray-50 tw-text-gray-500"><tr>
                    {[["Scheduler", "name"], ["Engaged", "engagedSeconds"], [singleDay ? "Activity · full day ET" : "Daily engaged time", null],
                      ["Call talk", "callSeconds"], ["Scheduled", "scheduled"], ["Texts", "texts"]].map(([label, key], i) => <th key={label}
                      className={`tw-px-4 tw-py-3 tw-font-medium tw-whitespace-nowrap ${i < 1 || !key ? "tw-text-left" : "tw-text-right"}
                        ${i === 0 ? "tw-sticky tw-left-0 tw-z-10 tw-bg-gray-50" : ""}`}
                      aria-sort={key === sort.key ? sort.direction === 1 ? "ascending" : "descending" : undefined}>
                      {key ? <button type="button" className="focus:tw-ring-2 focus:tw-ring-blue-500 tw-rounded" onClick={() => sortBy(key)}>
                        {label} {sort.key === key ? sort.direction === 1 ? "↑" : "↓" : "↕"}</button> : label}
                    </th>)}
                  </tr></thead>
                  <tbody>{visible.map((person) => {
                    const byDay = Object.fromEntries(person.days.map((day) => [day.day, day]));
                    const day = byDay[selection.from];
                    return <tr key={person.userId} className="tw-border-t tw-border-gray-100 hover:tw-bg-gray-50">
                      <td className="tw-px-4 tw-py-4 tw-sticky tw-left-0 tw-z-10 tw-bg-white">
                        <button type="button" onClick={() => openDetail(person)} className="tw-text-left tw-rounded focus:tw-ring-2 focus:tw-ring-blue-500">
                          <strong className="tw-block tw-text-blue-900 tw-break-words" title={person.name}>{nameOf(person)}</strong>
                          {singleDayScores && scores[String(person.userId)] !== undefined
                            ? <span className="tw-inline-block tw-mt-1"><ScoreBadge score={scores[String(person.userId)]} /></span> : null}
                          <span className="tw-block tw-text-xs tw-text-gray-500 tw-mt-1">{isToday ? <><span aria-hidden="true"
                            className={`tw-inline-block tw-w-2 tw-h-2 tw-rounded-full tw-mr-1 ${person.online ? "tw-bg-green-600" : "tw-bg-gray-400"}`} />
                          {person.online ? "Online at update" : "Offline at update"}</>
                            : person.days.length ? `${person.days.length} ${person.days.length === 1 ? "day" : "days"} with activity` : "No recorded time"}</span>
                        </button>
                      </td>
                      <td className="tw-px-4 tw-py-4 tw-text-right tw-whitespace-nowrap tw-font-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
                        {monitoringDuration(person.engagedSeconds)}</td>
                      <td className="tw-px-4 tw-py-4" style={{ minWidth: singleDay ? 330 : Math.max(330, dates.length * 25) }}>
                        {singleDay ? <><ActivityBar day={day || monitoringEmptyDay(selection.from)} compact onClick={() => openDetail(person, selection.from)} />
                          <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-400 tw-mt-1">
                            <span>{day ? `First ${monitoringTime(day.first)}` : "No recorded time"}</span><span>{day ? `Latest ${monitoringTime(day.last, day)}` : "—"}</span></div></>
                          : <div className="tw-flex tw-gap-1">{dates.map((date) => {
                            const seconds = byDay[date]?.engagedSeconds || 0;
                            return <button key={date} type="button" className="tw-flex-1 tw-text-center tw-rounded hover:tw-ring-2 hover:tw-ring-blue-400 focus:tw-ring-2 focus:tw-ring-blue-500"
                              onClick={() => openDetail(person, date)} aria-label={`${nameOf(person)}, ${monitoringDateLabel(date)}, ${monitoringDuration(seconds)} engaged`}
                              title={`${monitoringDateLabel(date, { weekday: "short" })}: ${monitoringDuration(seconds)}`}>
                              <span className="tw-block tw-h-6 tw-rounded-sm" style={{ background: seconds ? `rgba(22, 163, 74, ${0.2 + Math.min(seconds / 28800, 1) * 0.8})` : "#f0f2f4" }} />
                              <span className="tw-block tw-text-gray-400 tw-mt-1" style={{ fontSize: 10 }}>{Number(date.slice(-2))}</span>
                            </button>;
                          })}</div>}
                      </td>
                      <td className="tw-px-4 tw-py-4 tw-text-right tw-whitespace-nowrap">{monitoringDuration(person.callSeconds)}</td>
                      <td className="tw-px-4 tw-py-4 tw-text-right">{person.scheduled}</td><td className="tw-px-4 tw-py-4 tw-text-right">{person.texts}</td>
                    </tr>;
                  })}</tbody>
                </table>
                {!visible.length && <p className="tw-text-sm tw-text-gray-500 tw-p-8 tw-text-center">
                  {people.length ? "No schedulers match your search." : "No schedulers configured for this company."}</p>}
              </div>
              <div className="tw-px-4 tw-py-3 tw-border-t tw-border-gray-100 tw-text-sm tw-text-gray-600">
                Policies scheduled: <strong>{totals.scheduled}</strong> by schedulers + <strong>{data.otherScheduled}</strong> by other staff / unassigned = <strong>{data.totalScheduled}</strong> total.
              </div>
              <div className="tw-flex tw-flex-wrap tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-border-t tw-border-gray-100 tw-text-xs tw-text-gray-500">
                <span><span className="tw-inline-block tw-w-2 tw-h-2 tw-bg-green-600 tw-rounded-sm tw-mr-1" />{singleDay ? "Engaged periods" : "More colour = more engaged time"}
                  <span className="tw-inline-block tw-w-2 tw-h-2 tw-bg-gray-200 tw-rounded-sm tw-ml-4 tw-mr-1" />No recorded engaged time</span>
                <span>Click column headings to sort</span>
              </div>
            </div>
          </>}
    </div>
    {selectedPerson && <SchedulerTimeDetail key={`${selectedPerson.userId}-${selected.day || "period"}-${dayRegenToken}`} person={selectedPerson} selectedDay={selected.day}
      from={selection.from} to={selection.to} onClose={() => setSelected(null)}
      onScore={(userId, score) => setScores((prev) => ({ ...prev, [String(userId)]: score }))} />}
  </section>;
}
